/**
 * Mining beam: a pulsing energy line from the miner to the vein, with a
 * crackle of sparks at the cut point. One line + one sprite — cheap, and
 * it reads instantly as "extraction in progress".
 */
import * as THREE from "three";

export class MiningBeam {
  private line: THREE.Line;
  private spark: THREE.Sprite;
  private mat: THREE.LineBasicMaterial;
  private t = 0;

  constructor(scene: THREE.Scene) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
    this.mat = new THREE.LineBasicMaterial({
      color: 0xffc36a,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.line = new THREE.Line(geo, this.mat);
    this.line.frustumCulled = false;
    this.line.visible = false;
    scene.add(this.line);

    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.5, "rgba(255,200,120,0.5)");
    g.addColorStop(1, "rgba(255,180,80,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    this.spark = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: new THREE.CanvasTexture(canvas),
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
        opacity: 0,
      }),
    );
    this.spark.visible = false;
    scene.add(this.spark);
  }

  /** Draw the beam for this frame (render-space endpoints) or hide it. */
  update(dt: number, from: THREE.Vector3 | null, to: THREE.Vector3 | null): void {
    this.t += dt;
    if (!from || !to) {
      this.line.visible = false;
      this.spark.visible = false;
      this.mat.opacity = 0;
      return;
    }
    const pos = this.line.geometry.getAttribute("position") as THREE.BufferAttribute;
    pos.setXYZ(0, from.x, from.y, from.z);
    pos.setXYZ(1, to.x, to.y, to.z);
    pos.needsUpdate = true;
    this.mat.opacity = 0.55 + 0.3 * Math.sin(this.t * 17);
    this.line.visible = true;
    const flick = 2.2 + Math.sin(this.t * 23) * 1.1;
    this.spark.scale.set(flick, flick, 1);
    this.spark.position.copy(to);
    (this.spark.material as THREE.SpriteMaterial).opacity = 0.8;
    this.spark.visible = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.line);
    scene.remove(this.spark);
    this.line.geometry.dispose();
    this.mat.dispose();
    this.spark.material.dispose();
  }
}
